import { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { useFleetData } from '../state/useFleetData'
import { LoginPage } from '../auth/LoginPage'
import { useSession, type Session } from '../auth/useSession'
import { canResource, canSite } from '../auth/useCan'
import { allowedFleetIds, homePathFor, type FleetId } from '../cc/fleetNav'
import { trackScreenView } from '../lib/screenTelemetry'
import { ReportsShell } from '../reports/ReportsShell'
import { CommandCentre } from './CommandCentre'
import { SettingsShell } from '../settings/SettingsShell'
import { BusOpsShell } from '../busOps/BusOpsShell'
import { NagpurBusOpsShell } from '../busOps/NagpurBusOpsShell'
import { TuplOpsShell } from '../busOps/tupl/TuplOpsShell'
import { MbmtReportsShell } from '../mbmtReports/MbmtReportsShell'
import { EimShell } from '../eim/EimShell'
import { StarShell } from '../star/StarShell'
import { WonderCementShell } from '../wondercement/WonderCementShell'
import { GlobalShell } from '../global/GlobalShell'
import { Banners } from './Banners'

/** The three top-level areas -- kept as a type alias (not an enum of route paths) because
 *  a handful of components still name a destination by area rather than by URL, e.g. a
 *  non-admin redirect target. */
export type View = 'command' | 'reports' | 'settings'

/**
 * Command is the live wall (map + KPIs + replay). Reports is SQL width. Live map was
 * removed — Command already carries the live view.
 *
 * Routed rather than switched on local state: Back/Forward and a bookmark now land on the
 * actual page instead of always reopening at Command, and moving between areas pushes a
 * real history entry instead of silently swapping views underneath the same URL.
 */
export function AppShell(): JSX.Element {
  return (
    <Routes>
      <Route path="*" element={<GatedConsole />} />
    </Routes>
  )
}

function GatedConsole(): JSX.Element {
  const session = useSession()

  // THE GATE IS HERE, ABOVE useFleetData. Rendering the shell first and hiding it behind an
  // overlay would still open the SSE stream and fetch the fleet -- a signed-out browser
  // pulling live telemetry, with the map briefly visible underneath. Nothing loads until
  // there is a session.
  if (session.status === 'loading') return <SignInSplash />
  if (session.status === 'anonymous') {
    const params = new URLSearchParams(window.location.search)
    return (
      <LoginPage
        domain={session.domain}
        authError={session.error ?? params.get('auth_error') ?? undefined}
      />
    )
  }
  return <Console isAdmin={session.session.is_admin !== false} session={session.session} />
}

/** Held for the one request /api/me takes. A flash of the login page for somebody who is
 *  already signed in reads as having been logged out. */
function SignInSplash(): JSX.Element {
  return (
    <div style={{
      position: 'fixed', inset: 0, display: 'grid', placeItems: 'center',
      background: 'linear-gradient(160deg,#0B1120,#111C31 45%,#0A1526)',
    }}>
      <div style={{
        width: 26, height: 26, borderRadius: '50%',
        border: '2px solid rgba(56,189,248,.2)', borderTopColor: '#38BDF8',
        animation: 'spin .7s linear infinite',
      }} />
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  )
}

/** Sends a session away from a fleet it holds no site for. Courtesy -- the server 403s the
 *  fleet's own data regardless -- but an empty, errored page reads as a fault, so land
 *  on a fleet the account can actually use. */
function FleetGuard({ fleet, children }: { fleet: FleetId; children: JSX.Element }): JSX.Element {
  const state = useSession()
  const session = state.status === 'signed-in' ? state.session : undefined
  if (session && !allowedFleetIds(session).includes(fleet)) {
    return <Navigate to={homePathFor(session)} replace />
  }
  return children
}

function Console({ isAdmin, session }: { isAdmin: boolean; session: Session }): JSX.Element {
  useFleetData()
  const home = homePathFor(session)
  // From the session GatedConsole already resolved, synchronously -- NOT useCan /
  // useSiteAccess: each of those opens its own useSession(), which reads 'loading' for the
  // first render, so every gate below was false on mount and a reload or deep link to
  // /reports/... was <Navigate>d to /fleet before the session arrived.
  const hasReports = canResource(session, 'reports')
  const hasUltratechSite = canSite(session, 'ultratech')
  const hasWondercementSite = canSite(session, 'wondercement')
  const hasMbmtSite = canSite(session, 'mbmt')
  // Reports > Alerts covers Ultratech AND Wonder Cement (its router checks `?site=` per
  // account); every other tab is Ultratech-only and ReportsShell hides them itself. So
  // the route opens for either site -- a Wonder-Cement-only operations user lands on the
  // Alerts tab, not back on /fleet. Mirrors reports/reportsAccess.ts's canOpenReports.
  const canReports = hasReports && (hasUltratechSite || hasWondercementSite)
  const canMbmtReports = hasReports && hasMbmtSite

  // The ONE route-change beacon for the whole console (Sessions tab's Activity view) --
  // keyed on location.pathname, not on every render, same reasoning the comment below
  // gives for why this is one <Routes> tree and not several: a tab/mode change within
  // the SAME route match is a pathname change react-router already gives us, with
  // nothing else to key off that would not also fire on an unrelated re-render.
  const location = useLocation()
  useEffect(() => {
    trackScreenView(location.pathname)
  }, [location.pathname])

  return (
    // relative + h-full so Banners.tsx's `absolute left-3 top-3` anchors to the whole
    // app viewport (#root is height:100% per index.css) rather than nothing -- this is
    // the ONE mount point for every route, so a 403 toast is visible no matter which
    // site shell is open. Banners.tsx/addBanner already existed; nothing rendered them.
    <div className="relative h-full">
      <Banners />
      <Routes>
      <Route path="/" element={<Navigate to={home} replace />} />
      {/* ONE route per shell, with an OPTIONAL trailing segment (supported since
         react-router-dom 6.9) -- not two separate <Route> entries for the bare and
         param'd path. Two entries render the SAME component from two DIFFERENT route
         matches, and React Router remounts across a route-match change even when the
         element type is identical: the very first click from /reports to /reports/ops
         tore down and rebuilt the whole shell -- clock, collapsed-state, everything --
         which is what actually produced the "menu bar out of place" flash. One route
         means mode/tab/section is just a param change within the SAME match: no
         remount, ever. useParams() still reads it exactly the same either way. */}
      <Route path="/fleet/:tab?" element={<FleetGuard fleet="ultratech"><CommandCentre /></FleetGuard>} />
      {/* A department without the reports grant (or without any site the reports cover)
         lands back on the live wall, same reasoning /settings already gives for a
         non-admin -- an empty panel reads as a fault, not as "you don't have this." */}
      <Route path="/reports/:mode?" element={canReports ? <ReportsShell /> : <Navigate to="/fleet" replace />} />
      {/* A non-admin who lands on settings -- by deep link, or by a stale tab left open
         when their access was removed -- is put back on the live wall rather than shown
         an empty panel that reads as a fault. */}
      <Route path="/settings/:section?" element={isAdmin ? <SettingsShell /> : <Navigate to="/fleet" replace />} />
      {/* MBMT, Nagpur, EIM/Ports, STAR Cement, Global: every site is open to any signed-in
         user, same as Ultratech's own /fleet and /reports above -- these used to be gated
         on role==='super_admin' (server-side too, serving/app.py's/mbmt.py's
         _require_super_admin_raw_data), a deliberate restriction from when each site was
         added "ahead of merging this work to production." Confirmed lifted: every
         authenticated user should see every site, not just Ultratech. */}
      <Route path="/bus-ops/:tab?" element={<FleetGuard fleet="mbmt"><BusOpsShell /></FleetGuard>} />
      <Route path="/bus-ops-nagpur" element={<FleetGuard fleet="nagpur"><NagpurBusOpsShell /></FleetGuard>} />
      <Route path="/bus-ops-tupl/:tab?" element={<FleetGuard fleet="tupl"><TuplOpsShell /></FleetGuard>} />
      {/* No reports shell for TUPL yet (SPEC.md Non-goals) -- same "feature not built,
          redirect to Live" pattern MBMT's own /mbmt/charging route already uses below. */}
      <Route path="/tupl/reports/:mode?" element={<Navigate to="/bus-ops-tupl" replace />} />
      <Route path="/mbmt/reports/:mode?" element={canMbmtReports ? <MbmtReportsShell /> : <Navigate to="/bus-ops" replace />} />
      {/* Charging is hidden from MBMT's nav (fleetNav.surfacePills), so a bookmark or stale
         tab lands back on MBMT Live instead of a page with no way to reach it. The
         shell itself is kept in the tree for when it comes back. */}
      <Route path="/mbmt/charging/:tab?" element={<Navigate to="/bus-ops" replace />} />
      {/* Reports is admin-only at every site: a non-admin on /eim/reports or /star/reports
         (bookmark, stale tab) is put back on that site's Live view. Same route match as
         Live, so the guard costs no remount when an admin switches between the two. */}
      <Route path="/eim/:tab?" element={<FleetGuard fleet="eim"><ReportsTabGuard allowed={hasReports} base="/eim"><EimShell /></ReportsTabGuard></FleetGuard>} />
      <Route path="/star/:tab?" element={<FleetGuard fleet="star"><ReportsTabGuard allowed={hasReports} base="/star"><StarShell /></ReportsTabGuard></FleetGuard>} />
      <Route path="/wondercement" element={<FleetGuard fleet="wondercement"><WonderCementShell /></FleetGuard>} />
      <Route path="/global" element={<FleetGuard fleet="global"><GlobalShell /></FleetGuard>} />
      <Route path="*" element={<Navigate to={home} replace />} />
      </Routes>
    </div>
  )
}

/** Sends a non-admin on a site's `reports` tab back to that site's base path. */
function ReportsTabGuard({ allowed, base, children }: { allowed: boolean; base: string; children: JSX.Element }): JSX.Element {
  const { tab } = useParams()
  if (tab === 'reports' && !allowed) return <Navigate to={base} replace />
  return children
}
